import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/adminApi";
import { Field, inputCls, btnPrimary, btnGhost } from "./ui";

const EMPTY = {
  id: null, type: "column", title: "", dek: "", body: "",
  pull_quote: "", tags: "", slug: "", published_at: "",
};

const postToForm = (p) => ({
  id: p.id,
  type: p.type || "column",
  title: p.title || "",
  dek: p.dek || "",
  body: p.body || "",
  pull_quote: p.pull_quote || "",
  tags: (p.tags || []).join(", "),
  slug: p.slug || "",
  published_at: p.published_at ? p.published_at.slice(0, 16) : "",
});

const formToPayload = (f) => ({
  type: f.type,
  title: f.title || null,
  dek: f.dek || null,
  body: f.body,
  pull_quote: f.pull_quote || null,
  tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
  slug: f.slug || null,
  published_at: f.published_at ? new Date(f.published_at).toISOString() : null,
});

export default function ReportPanel() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    adminApi.get("/report").then((r) => setPosts(r.posts)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.body.trim()) return toast.error("The post needs a body.");
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (form.id) {
        await adminApi.put(`/report/${form.id}`, payload);
        toast.success("Post updated.");
      } else {
        await adminApi.post("/report", payload);
        toast.success("Post published to The Shi Report.");
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await adminApi.del(`/report/${p.id}`);
      toast.success("Post deleted.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fmtDate = (iso) => {
    try { return format(new Date(iso), "MMM d, yyyy"); } catch { return ""; }
  };

  return (
    <div data-testid="admin-report-panel">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-latte">{posts.length} post(s) in The Shi Report</p>
        <button
          data-testid="admin-new-post-button"
          onClick={() => setForm({ ...EMPTY })}
          className={`${btnPrimary} inline-flex items-center gap-2`}
        >
          <Plus size={16} /> New Post
        </button>
      </div>

      {form && (
        <div data-testid="admin-post-form" className="mt-8 rounded-2xl border border-gold/30 bg-cocoa p-6 md:p-10">
          <h3 className="font-display text-2xl font-bold text-cream">
            {form.id ? "Edit Post" : "New Post"}
          </h3>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Field label="Type">
              <select
                data-testid="post-form-type"
                className={inputCls}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="column">Column (magazine style)</option>
                <option value="quick">Quick Thought (social style)</option>
              </select>
            </Field>
            <Field label="Publish Date (optional)">
              <input data-testid="post-form-date" type="datetime-local" className={inputCls} value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} />
            </Field>
            {form.type === "column" && (
              <>
                <Field label="Title" className="md:col-span-2">
                  <input data-testid="post-form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </Field>
                <Field label="Dek (italic subheading)" className="md:col-span-2">
                  <input data-testid="post-form-dek" className={inputCls} value={form.dek} onChange={(e) => setForm({ ...form, dek: e.target.value })} />
                </Field>
              </>
            )}
            <Field label="Body (blank line between paragraphs)" className="md:col-span-2">
              <textarea data-testid="post-form-body" rows={7} className={inputCls} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </Field>
            {form.type === "column" && (
              <Field label="Pull Quote (optional)" className="md:col-span-2">
                <input data-testid="post-form-pull-quote" className={inputCls} value={form.pull_quote} onChange={(e) => setForm({ ...form, pull_quote: e.target.value })} />
              </Field>
            )}
            <Field label="Tags (comma separated)">
              <input data-testid="post-form-tags" className={inputCls} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="culture, music" />
            </Field>
            <Field label="Slug (optional)">
              <input data-testid="post-form-slug" className={inputCls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </Field>
          </div>
          <div className="mt-8 flex gap-3">
            <button data-testid="post-form-save-button" onClick={save} disabled={saving} className={btnPrimary}>
              {saving ? "Saving…" : "Save Post"}
            </button>
            <button data-testid="post-form-cancel-button" onClick={() => setForm(null)} className={btnGhost}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 divide-y divide-corduroy rounded-2xl border border-corduroy">
        {posts.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-4 px-6 py-5">
            <div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${p.type === "quick" ? "bg-neon/15 text-neon border border-neon/40" : "bg-gold/15 text-gold border border-gold/40"}`}>
                  {p.type === "quick" ? "Quick" : "Column"}
                </span>
                <span className="text-xs text-latte">{fmtDate(p.published_at)}</span>
              </div>
              <p className="mt-1.5 font-display text-lg font-bold text-cream">
                {p.type === "quick" ? p.body.slice(0, 70) + (p.body.length > 70 ? "…" : "") : p.title}
              </p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`admin-post-edit-${p.slug}`} onClick={() => setForm(postToForm(p))} className="flex h-10 w-10 items-center justify-center rounded-full border border-corduroy text-cream transition-colors hover:border-gold hover:text-gold" aria-label="Edit post">
                <Pencil size={15} />
              </button>
              <button data-testid={`admin-post-delete-${p.slug}`} onClick={() => remove(p)} className="flex h-10 w-10 items-center justify-center rounded-full border border-corduroy text-cream transition-colors hover:border-wine hover:bg-wine" aria-label="Delete post">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
